export type ViewerState = Record<string, unknown>

export type Vector3 = {
  x: number
  y: number
  z: number
}

export type Vector4 = {
  w: number
  x: number
  y: number
  z: number
}

export type Quaternion = {
  w: number
  x: number
  y: number
  z: number
}

export type ViewerBounds = {
  union: (bounds: ViewerBounds) => void
  isEmpty: () => boolean
  getCenter: (target: Vector3) => Vector3
}

export type ViewerMouseEvent = {
  canvasX?: number
  canvasY?: number
}

export type ViewerSelectionChangeEvent = {
  dbIdArray?: number[]
  nodeArray?: number[]
}

export type ViewerEventHandler = (event?: ViewerSelectionChangeEvent) => void | Promise<void>

export type ViewerProperty = {
  attributeName?: string
  displayName?: string
  displayValue?: unknown
}

export type ViewerPropertyResult = {
  name?: string
  properties?: ViewerProperty[]
}

export type ViewerDocument = {
  getRoot: () => { getDefaultGeometry: () => unknown }
}

export type StoredFragmentTransform = {
  rotation: Quaternion
  scale: Vector3
  translation: Vector3
}

export type TransformMesh = {
  position: Vector3
}

export type TransformControl = {
  addEventListener: (event: string, callback: () => void) => void
  attach: (mesh: TransformMesh) => void
  dispose?: () => void
  onPointerDown: (event: ViewerMouseEvent) => boolean
  onPointerHover: (event: ViewerMouseEvent) => boolean
  onPointerMove: (event: ViewerMouseEvent) => boolean
  onPointerUp: (event: ViewerMouseEvent) => boolean
  removeEventListener: (event: string, callback: () => void) => void
  setPosition?: (position: Vector3) => void
  setSize?: (size: number) => void
  update?: () => void
  visible: boolean
}

export type ToolbarControl = {
  addControl?: (control: ToolbarButton) => void
  container?: HTMLElement
  getControl?: (controlId: string) => ToolbarControl | null
  registerCustomizeToolbarCB?: (callback: () => void) => void
  setDisplay?: (display: string) => void
  setVisible?: (visible: boolean) => void
}

export type ToolbarButton = ToolbarControl & {
  addClass?: (className: string) => void
  onClick?: () => void
  removeClass?: (className: string) => void
  setState?: (state: number) => void
  setIcon?: (iconClass: string) => void
  setToolTip?: (tooltip: string) => void
}

export type ViewerTool = {
  activate: () => void
  deactivate: () => void
  getName: () => string
  getNames: () => string[]
  getPriority?: () => number
  handleButtonDown: (event: ViewerMouseEvent, button: number) => boolean
  handleButtonUp: (event?: ViewerMouseEvent, button?: number) => boolean
  handleMouseMove: (event: ViewerMouseEvent) => boolean
  handleSingleClick?: (event: ViewerMouseEvent, button: number) => boolean
  handleSingleTap?: (event: ViewerMouseEvent) => boolean
}

export type ViewerModel = {
  getData?: () => {
    instanceTree?: {
      enumNodeFragments: (dbId: number, callback: (fragId: number) => void, recursive: boolean) => void
      enumNodeChildren?: (dbId: number, callback: (dbId: number) => void, recursive: boolean) => void
      getRootId?: () => number
      getNodeParentId?: (dbId: number) => number
      getNodeName?: (dbId: number) => string
    }
  }
  getFragmentList?: () => {
    getCount?: () => number
    db2ThemingColor?: Record<string, Vector4>
    getAnimTransform: (fragId: number, scale: Vector3, rotation: Quaternion, translation: Vector3) => boolean
    getWorldBounds: (fragId: number, bounds: ViewerBounds) => void
    updateAnimTransform: (fragId: number, scale: Vector3, rotation: Quaternion, translation: Vector3) => void
  }
}

export type ViewerInstance = {
  loadDocumentNode: (document: ViewerDocument, node: unknown) => Promise<ViewerModel>
  waitForLoadDone: (options: { geometry: boolean, propDb: boolean, textures: boolean, onlyModels: ViewerModel[] }) => Promise<void>
  resize?: () => void
  addEventListener: (event: string, callback: ViewerEventHandler) => void
  clearSelection?: () => void
  clientToWorld?: (canvasX: number, canvasY: number) => { dbId?: number } | null
  explode?: (scale: number) => void
  finish: () => void
  fitToView?: (objectIds?: number[], model?: ViewerModel, immediate?: boolean) => void
  getExtension?: (extensionId: string) => unknown
  getExplodeScale?: () => number
  getHiddenNodes?: () => number[]
  getIsolatedNodes?: () => number[]
  getSelection?: () => number[]
  getProperties?: (
    dbId: number,
    onSuccess: (result: ViewerPropertyResult) => void,
    onError?: (error: unknown) => void
  ) => void
  getState?: (filter?: Record<string, boolean>) => ViewerState
  getScreenShot?: (width: number, height: number, callback: (blobUrl: string) => void) => void
  getToolbar?: () => ToolbarControl | null
  hide?: (dbIds: number[]) => void
  impl?: {
    visibilityManager?: {
      setNodeOff: (dbId: number, off: boolean, model?: ViewerModel, children?: number[], fragments?: number[]) => void
      setVisibilityOnNode: (dbId: number, visible: boolean, model?: ViewerModel, skipEventing?: boolean, recursive?: boolean) => void
    }
    canvasBoundingclientRectDirty?: boolean
    addOverlay?: (sceneName: string, object: unknown) => void
    camera?: unknown
    canvas?: HTMLCanvasElement
    createOverlayScene?: (sceneName: string) => void
    invalidate?: (needsClear: boolean, needsRender: boolean, overlayDirty: boolean) => void
    matman?: () => {
      addMaterial?: (name: string, material: unknown, skipHeuristics: boolean) => void
    }
    removeOverlay?: (sceneName: string, object: unknown) => void
    removeOverlayScene?: (sceneName: string) => void
    sceneUpdated?: (needsRender: boolean) => void
  }
  loadExtension?: (extensionId: string) => Promise<unknown>
  model?: ViewerModel
  prefs?: {
    get: (name: string) => unknown
  }
  removeEventListener: (event: string, callback: ViewerEventHandler) => void
  restoreState?: (state: ViewerState, filter?: Record<string, boolean>, immediate?: boolean) => boolean
  select?: (dbIds: number[], model?: ViewerModel, selectionType?: number) => void
  setGhosting?: (value: boolean) => void
  setThemingColor?: (
    dbId: number,
    color: Vector4 | null,
    model?: ViewerModel,
    recursive?: boolean
  ) => void
  setActiveNavigationTool?: (tool: string) => boolean
  showAll?: () => void
  lockExplode?: (dbIds: number[], locked: boolean, model?: ViewerModel) => boolean
  show?: (dbIds: number[]) => void
  start: () => number
  isolate?: (dbIds: number[]) => void
  toolController?: {
    activateTool: (toolName: string) => boolean
    deactivateTool: (toolName: string) => boolean
    registerTool: (tool: ViewerTool) => boolean
  }
}

export type BoxSelectionExtension = {
  addToolbarButton: (add: boolean) => void
}

export type ExplodeExtension = {
  activate: () => boolean
  deactivate: () => boolean
  isActive: () => boolean
  getScale: () => number
  setScale: (scale: number) => void
  setStrategy: (strategy: string) => void
}

export type SectionExtension = {
  setSectionFromPlane: (cutplane: null) => void
}

export type ViewerOptions = {
  env: string
  api?: string
  getAccessToken?: (callback: (token: string, expiresIn: number) => void) => void
}

export type AutodeskViewing = {
  Private?: {
    TransformControls?: new (camera: unknown, canvas: HTMLCanvasElement, mode: string) => TransformControl
  }
  Document: {
    load: (urn: string, onSuccess: (document: ViewerDocument) => void, onError: () => void) => void
  }
  GuiViewer3D: new (container: HTMLElement, config?: Record<string, unknown>) => ViewerInstance
  Initializer: (options: ViewerOptions, callback: () => void) => void
  CAMERA_CHANGE_EVENT?: string
  SELECTION_CHANGED_EVENT?: string
  SelectionType?: { OVERLAYED: number }
  TOOLBAR_CREATED_EVENT: string
  UI?: {
    Button: new (controlId: string) => ToolbarButton
    ButtonState?: {
      ACTIVE: number
      DISABLED: number
      INACTIVE: number
    }
  }
}

const autodeskViewerVersion = '7.120.0'
const autodeskViewerBaseUrl = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${autodeskViewerVersion}`
const autodeskViewerCssUrl = `${autodeskViewerBaseUrl}/style.min.css`
const autodeskViewerScriptUrl = `${autodeskViewerBaseUrl}/viewer3D.min.js`
let autodeskViewerAssetsPromise: Promise<void> | null = null

export function getAutodeskViewing () {
  return (window as Window & { Autodesk?: { Viewing?: AutodeskViewing } }).Autodesk?.Viewing
}

export function invalidateViewer (viewer: ViewerInstance | null) {
  viewer?.impl?.invalidate?.(true, true, true)
  viewer?.impl?.sceneUpdated?.(true)
}

export function cloneTransform (transform: StoredFragmentTransform): StoredFragmentTransform {
  return {
    rotation: { ...transform.rotation },
    scale: { ...transform.scale },
    translation: { ...transform.translation }
  }
}

export function getThree () {
  return (window as Window & {
    THREE?: {
      Box3: new () => ViewerBounds
      Mesh: new (geometry: unknown, material: unknown) => TransformMesh
      MeshPhongMaterial: new (options: Record<string, unknown>) => unknown
      Quaternion: new (x?: number, y?: number, z?: number, w?: number) => Quaternion
      SphereGeometry: new (radius: number, widthSegments: number, heightSegments: number) => unknown
      Vector3: new (x?: number, y?: number, z?: number) => Vector3
      Vector4: new (x?: number, y?: number, z?: number, w?: number) => Vector4
    }
  }).THREE
}

export function createVector3 (vector: Vector3): Vector3 {
  const THREE = getThree()
  return THREE ? new THREE.Vector3(vector.x, vector.y, vector.z) : { ...vector }
}

export function createVector4 (vector: Vector4): Vector4 {
  const THREE = getThree()
  return THREE
    ? new THREE.Vector4(vector.x, vector.y, vector.z, vector.w)
    : { ...vector }
}

export function createQuaternion (rotation: Quaternion): Quaternion {
  const THREE = getThree()
  return THREE ? new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w) : { ...rotation }
}

function loadStylesheet () {
  return new Promise<void>((resolve, reject) => {
    if (document.querySelector(`link[href="${autodeskViewerCssUrl}"]`)) {
      resolve()
      return
    }

    const link = document.createElement('link')
    link.href = autodeskViewerCssUrl
    link.rel = 'stylesheet'
    link.onload = () => resolve()
    link.onerror = () => {
      link.remove()
      reject(new Error('Failed to load Autodesk Viewer stylesheet.'))
    }
    document.head.appendChild(link)
  })
}

function loadScript () {
  return new Promise<void>((resolve, reject) => {
    if (getAutodeskViewing()) {
      resolve()
      return
    }

    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${autodeskViewerScriptUrl}"]`)
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Autodesk Viewer script.')), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = autodeskViewerScriptUrl
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      script.remove()
      reject(new Error('Failed to load Autodesk Viewer script.'))
    }
    document.head.appendChild(script)
  })
}

export function loadAutodeskViewerAssets () {
  if (!autodeskViewerAssetsPromise) {
    autodeskViewerAssetsPromise = Promise.all([
      loadStylesheet(),
      loadScript()
    ])
      .then(() => undefined)
      .catch((error) => {
        autodeskViewerAssetsPromise = null
        throw error
      })
  }

  return autodeskViewerAssetsPromise
}

function getComparablePropertyName (name: string) {
  return name.trim().toLocaleLowerCase()
}

function normalizePropertyName (name: string) {
  return getComparablePropertyName(name)
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}]/gu, '')
}

function propertyNameMatches (propertyName: string, expectedName: string) {
  const comparablePropertyName = getComparablePropertyName(propertyName)
  const comparableExpectedName = getComparablePropertyName(expectedName)
  if (comparablePropertyName === comparableExpectedName) return true

  const normalizedExpectedName = normalizePropertyName(expectedName)
  return normalizedExpectedName.length > 0 &&
    normalizePropertyName(propertyName) === normalizedExpectedName
}

export function findPropertyValue (
  properties: ViewerProperty[],
  names: Array<string | null | undefined>
) {
  for (const name of names) {
    if (!name) continue

    const property = properties.find(({ attributeName, displayName }) => {
      return [attributeName, displayName].some(propertyName =>
        propertyNameMatches(String(propertyName ?? ''), name)
      )
    })
    const value = property?.displayValue
    if (value !== undefined && value !== null && value !== '') return String(value)
  }

  return null
}
